import Sticky from "react-stickynode"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import dynamic from "next/dynamic"
import MetaData from "@/common/components/MetaData"
import SectionLine from "@/common/components/_UI/SectionLine"

import ComplianceAPIDark from "@/common/assets/image/products/icons/ComplianceAPIDark.svg"
import KYA from "@/common/assets/image/products/compliance/api/KYA.webp"
import KYB from "@/common/assets/image/products/compliance/api/KYB.webp"
import KYC from "@/common/assets/image/products/compliance/api/KYC.webp"

const NavbarNew = dynamic(import("@/containers/common/NavbarNew"))

import { DarkBGWrapper, LayoutWrapper } from "@/common/components/LayoutStyle"
import ProductCTA from "@/common/components/ProductCTA"
import ProductViewMore from "@/common/components/ProductViewMore"
import { ProductBanner } from "@/common/components/ProductBanner"

import { ProductNames, SolutionNames } from "@/common/data/Products"
import Footer from "@/containers/common/FooterNew"
import NextImg from "@/common/components/NextImg"

const ComplianceAPIPage = () => {
  const { t } = useTranslation(["complianceAPI"])
  const { t: commonT } = useTranslation(["common"])

  return (
    <>
      <MetaData
        title={t("meta.title")}
        description={t("meta.description")}
        url={t("meta.url")}
        image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
        languageAlternates={[
          {
            hrefLang: "en-US",
            href: "https://www.kryptogo.com/products/compliance-api"
          },
          {
            hrefLang: "zh-TW",
            href: "https://www.kryptogo.com/tw/products/compliance-api"
          },
          {
            hrefLang: "zh-CN",
            href: "https://www.kryptogo.com/cn/products/compliance-api"
          },
          {
            hrefLang: "ja-JP",
            href: "https://www.kryptogo.com/ja/products/compliance-api"
          }
        ]}
      />

      <Sticky top={0} innerZ={1000} activeClass="sticky-active">
        <NavbarNew />
      </Sticky>
      <DarkBGWrapper></DarkBGWrapper>
      <LayoutWrapper>
        <ProductBanner
          icon={ComplianceAPIDark.src}
          bannerType={ProductNames.ComplianceAPI}
          title={t("Banner.productName")}
          titleh1={t("Banner.title")}
          description={t("Banner.description")}
          primaryButton={{
            content: t("Banner.button"),
            href: "https://www.kryptogo.com/docs/developers/4-4-compliance-apis"
          }}
        />
        <SectionLine />
        <ProductBanner
          short
          paddingTop="0"
          title={t("Banner.1.productName")}
          titleType="primary"
          titleh3={t("Banner.1.title")}
          titleh3Type="normal"
          description={t("Banner.1.description")}
          comp2={<NextImg src={KYA.src} />}
          primaryButton={{
            content: t("Banner.1.button"),
            href: "https://www.kryptogo.com/docs/kyabc#tag/DD-KYA"
          }}
        />
        <ProductBanner
          short
          paddingTop="0"
          title={t("Banner.2.productName")}
          titleType="primary"
          titleh3={t("Banner.2.title")}
          titleh3Type="normal"
          description={t("Banner.2.description")}
          comp2={<NextImg src={KYB.src} />}
          primaryButton={{
            content: t("Banner.2.button"),
            href: "https://www.kryptogo.com/docs/kyabc#tag/DD-KYB-and-KYC"
          }}
        />
        <ProductBanner
          short
          paddingTop="0"
          title={t("Banner.3.productName")}
          titleType="primary"
          titleh3={t("Banner.3.title")}
          titleh3Type="normal"
          description={t("Banner.3.description")}
          comp2={<NextImg src={KYC.src} />}
          primaryButton={{
            content: t("Banner.3.button"),
            href: "https://www.kryptogo.com/docs/kyabc#tag/DD-KYB-and-KYC"
          }}
          subtleButton={{
            content: t("Banner.3.subtleButton"),
            href: "https://www.kryptogo.com/docs/kyabc#tag/ID-Verification"
          }}
        />
        <ProductCTA
          title={t("CTA.title")}
          desc={t("CTA.desc")}
          primaryButton={{ text: t("CTA.button") }}
          secondaryButton={{
            text: t("CTA.secondaryButton"),
            href: "https://www.kryptogo.com/docs/developers/4-4-compliance-apis"
          }}
        />
        <ProductViewMore
          commonT={commonT}
          title={commonT("ViewMore.title")}
          tab={[
            {
              name: commonT("ViewMore.products"),
              content: [
                ProductNames.CompliancePro,
                ProductNames.ComplianceLite,
                ProductNames.OAuthSDK
              ]
            },
            {
              name: commonT("ViewMore.solutions"),
              content: [SolutionNames.Defi]
            }
          ]}
          actionText={commonT("ViewMore.allProducts")}
        ></ProductViewMore>
        <Footer />
      </LayoutWrapper>
    </>
  )
}

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, [
      "common",
      "contactUs",
      "compliance",
      "complianceAPI"
    ]))
  }
})

export default ComplianceAPIPage
